import { DevicePhoneMobileIcon } from "@heroicons/react/24/outline";

import { ITillReader } from "../../models/till/ITillConfig";
import classNames from "../../utils/classNames";
import StatusPill, { StatusPillColour } from "../shared/StatusPill";

interface TillReaderSelectProps {
  readers: ITillReader[];
  value: string | null;
  onChange: (readerId: string) => void;
  disabled?: boolean;
}

/** Pick which Stripe reader takes the payment. Offline readers are shown but can't be chosen. */
export default function TillReaderSelect({ readers, value, onChange, disabled }: TillReaderSelectProps) {
  if (readers.length === 0) {
    return (
      <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
        No card readers were found on the connected Stripe account. Register one in that account&rsquo;s Stripe Dashboard under Terminal → Readers, then reload the till.
      </p>
    );
  }

  return (
    <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {readers.map((reader) => {
        const online = reader.status === "online";
        const selected = value === reader.id;
        return (
          <li key={reader.id}>
            <button
              type="button"
              disabled={disabled || !online}
              onClick={() => onChange(reader.id)}
              className={classNames(
                "w-full flex items-center gap-3 rounded-lg border p-3 text-left min-h-[4rem]",
                selected ? "border-primary-500 ring-2 ring-primary-200 bg-primary-50" : "border-gray-200 bg-white",
                !online ? "opacity-60 cursor-not-allowed" : "hover:border-primary-300"
              )}
            >
              <DevicePhoneMobileIcon className="h-8 w-8 shrink-0 text-gray-500" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{reader.label || reader.id}</p>
                <p className="text-xs text-gray-500 truncate">{reader.deviceType}{reader.serialNumber ? ` · ${reader.serialNumber}` : ""}</p>
              </div>
              <StatusPill text={online ? "Online" : "Offline"} colour={online ? StatusPillColour.Green : StatusPillColour.Red} />
            </button>
          </li>
        );
      })}
    </ul>
  );
}
